/**
 * Phone chrome: a compact top bar and a thumb-reachable bottom tab bar.
 *
 * The desktop packs nav, search, AOR and identity into one 58px header. A phone
 * cannot, so it splits: the top bar carries identity and link state, the bottom
 * bar carries navigation where the thumb is, and the AOR switcher becomes a
 * sheet. None of the command-centre chrome appears on the strategic entry
 * globe — the same rule as the desktop, so you fly into an AOR first.
 */

import { Ellipsis, LogOut, Search } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { AORS } from '@/map/aors';
import { cn } from '@/lib/utils';
import { useAorScopedCount } from '@/features/fleet/aorScope';
import { splitTabs, useAvailableScreens } from '@/features/workspace/screens';
import { useAuthStore } from '@/stores/authStore';
import { useLinkStatus } from '@/stores/droneStore';
import { useMapStore } from '@/stores/mapStore';
import { useScreen, useWorkspaceStore } from '@/stores/workspaceStore';

/** Five cells at 360px is 72px each, the narrowest a label and icon read at. */
const TAB_SLOTS = 5;

function Reticle() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" className="text-foreground">
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="12" cy="12" r="3.2" fill="currentColor" />
      <line x1="12" y1="1" x2="12" y2="6" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function MobileLive() {
  const status = useLinkStatus();
  const count = useAorScopedCount();
  const live = status === 'open';
  const color = status === 'closed' ? 'var(--danger)' : live ? 'var(--ok)' : 'var(--caution)';
  return (
    <span className="flex items-center gap-1.5 whitespace-nowrap">
      <span
        className={cn('size-[7px] rounded-full', !live && 'px-pulse')}
        style={{ background: color, boxShadow: live ? `0 0 7px ${color}` : undefined }}
      />
      <span className="tnum text-[11px]" style={{ color: 'var(--muted-foreground-3)' }}>
        {count} AC
      </span>
    </span>
  );
}

function AorSheet({ onClose }: { onClose: () => void }) {
  const aor = useWorkspaceStore((state) => state.aor);
  const setAor = useWorkspaceStore((state) => state.setAor);
  const setScreen = useWorkspaceStore((state) => state.setScreen);
  const flyTo = useMapStore((state) => state.flyTo);

  const pick = (id: string) => {
    setScreen('command');
    setAor(id);
    flyTo(id);
    onClose();
  };

  return (
    <div
      className="absolute inset-0 z-[70] flex items-end"
      style={{ background: 'rgba(6,6,8,0.55)', backdropFilter: 'blur(6px)' }}
      onClick={onClose}
    >
      <div
        className="glass scroll-thin m-2 mb-[calc(var(--mobile-nav-h)+env(safe-area-inset-bottom)+8px)] flex max-h-[calc(100dvh-var(--mobile-nav-h)-var(--safe-t)-var(--safe-b)-24px)] w-full flex-col gap-1 overflow-y-auto rounded-[20px] p-2"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="label-mono px-3 pt-2 pb-1 text-[9px] text-[color:var(--muted-foreground-2)]">
          SELECT AREA OF RESPONSIBILITY
        </div>
        {AORS.map((a) => (
          <button
            key={a.id}
            type="button"
            onClick={() => pick(a.id)}
            className="flex items-center gap-2 rounded-[13px] px-3 py-3 text-left"
            style={{ background: aor === a.id ? 'oklch(1 0 0 / 0.1)' : 'transparent' }}
          >
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="label-mono text-[12px]">{a.id}</span>
              <span className="label-mono text-[10px] text-[color:var(--muted-foreground)]">
                {a.sub}
              </span>
            </span>
            {aor === a.id ? <span className="ml-auto text-[12px]">✓</span> : null}
          </button>
        ))}
      </div>
    </div>
  );
}

export function MobileTopBar({ onChangePassword }: { onChangePassword?: () => void }): ReactNode {
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const entered = useWorkspaceStore((state) => state.entered);
  const aor = useWorkspaceStore((state) => state.aor);
  const openSearch = useWorkspaceStore((state) => state.openSearch);
  const [aorOpen, setAorOpen] = useState(false);
  const initials = (user?.username ?? '??').slice(0, 2).toUpperCase();

  return (
    <header
      className="glass absolute top-2 z-40 flex h-[52px] items-center gap-2 rounded-[18px] px-3"
      style={{
        marginTop: 'env(safe-area-inset-top)',
        // Landscape puts the notch on a side edge; keep the bar off it.
        left: 'calc(var(--safe-l) + 8px)',
        right: 'calc(var(--safe-r) + 8px)',
      }}
    >
      <Reticle />
      {entered ? (
        <button
          type="button"
          onClick={() => setAorOpen(true)}
          aria-label={`Area of responsibility: ${aor}`}
          className="hit-44 flex min-w-0 items-center gap-1.5 rounded-[99px] border border-[color:var(--border)] bg-[oklch(1_0_0/0.07)] px-3 py-1.5 label-mono text-[10px]"
        >
          <span className="truncate">{aor}</span>
          <span style={{ color: 'var(--muted-foreground)' }}>▾</span>
        </button>
      ) : (
        <span className="truncate label-mono text-[9px] text-[color:var(--muted-foreground-3)]">
          SELECT AN AOR
        </span>
      )}

      <div className="ml-auto flex shrink-0 items-center gap-3">
        <MobileLive />
        {entered ? (
          <button
            type="button"
            aria-label="Search"
            onClick={openSearch}
            className="hit-44 text-[color:var(--muted-foreground)]"
          >
            <Search className="size-[18px]" />
          </button>
        ) : null}
        <button
          type="button"
          aria-label="Change password"
          title={user?.username}
          onClick={onChangePassword}
          className="hit-44 inline-flex size-[28px] items-center justify-center rounded-full border border-[color:var(--border-strong)] bg-[oklch(1_0_0/0.12)] label-mono text-[10px]"
        >
          {initials}
        </button>
        <button
          type="button"
          aria-label="Sign out"
          onClick={() => void logout()}
          className="hit-44 text-[color:var(--muted-foreground)]"
        >
          <LogOut className="size-[18px]" />
        </button>
      </div>

      {aorOpen ? <AorSheet onClose={() => setAorOpen(false)} /> : null}
    </header>
  );
}

export function MobileTabBar(): ReactNode {
  const active = useScreen();
  const setScreen = useWorkspaceStore((state) => state.setScreen);
  const entered = useWorkspaceStore((state) => state.entered);
  const screens = useAvailableScreens();
  const [moreOpen, setMoreOpen] = useState(false);

  if (!entered) return null;

  const { tabs, overflow } = splitTabs(screens, TAB_SLOTS);
  const moreActive = overflow.some((screen) => screen.id === active);

  const cell = (
    key: string,
    label: string,
    Icon: typeof Ellipsis,
    isActive: boolean,
    onClick: () => void,
    extra?: { expanded?: boolean },
  ) => (
    <button
      key={key}
      type="button"
      aria-current={isActive && extra === undefined ? 'page' : undefined}
      aria-expanded={extra?.expanded}
      onClick={onClick}
      className="flex min-w-0 flex-1 flex-col items-center justify-center gap-1"
      style={{ color: isActive ? 'var(--foreground)' : 'var(--muted-foreground-3)' }}
    >
      <Icon className="size-[20px]" strokeWidth={isActive ? 2.4 : 1.8} />
      <span className="max-w-full truncate label-mono text-[8px]" style={{ letterSpacing: '0.04em' }}>
        {label}
      </span>
      <span
        className="h-[2px] w-5 rounded-full"
        style={{ background: isActive ? 'var(--foreground)' : 'transparent' }}
      />
    </button>
  );

  return (
    <>
      <nav
        className="glass absolute inset-x-0 bottom-0 z-40 flex items-stretch justify-around rounded-none border-t border-[color:var(--border)]"
        style={{
          height: 'calc(var(--mobile-nav-h) + env(safe-area-inset-bottom))',
          paddingBottom: 'env(safe-area-inset-bottom)',
          paddingLeft: 'calc(var(--safe-l) + 4px)',
          paddingRight: 'calc(var(--safe-r) + 4px)',
        }}
        aria-label="Screens"
      >
        {tabs.map((tab) =>
          cell(tab.id, tab.short ?? tab.label, tab.icon, active === tab.id, () => {
            setMoreOpen(false);
            setScreen(tab.id);
          }),
        )}
        {overflow.length
          ? cell('more', 'More', Ellipsis, moreActive || moreOpen, () => setMoreOpen((v) => !v), {
              expanded: moreOpen,
            })
          : null}
      </nav>

      {moreOpen ? (
        <div
          className="absolute inset-0 z-[39] flex items-end"
          style={{ background: 'rgba(6,6,8,0.45)' }}
          onClick={() => setMoreOpen(false)}
        >
          <div
            className="glass scroll-thin mx-2 mb-[calc(var(--mobile-nav-h)+env(safe-area-inset-bottom)+8px)] flex max-h-[calc(100dvh-var(--mobile-nav-h)-var(--safe-t)-var(--safe-b)-80px)] w-full flex-col gap-1 overflow-y-auto rounded-[20px] p-2"
            onClick={(event) => event.stopPropagation()}
            aria-label="More screens"
          >
            <div className="label-mono px-3 pt-2 pb-1 text-[9px] text-[color:var(--muted-foreground-2)]">
              MORE SCREENS
            </div>
            {overflow.map((screen) => {
              const Icon = screen.icon;
              const isActive = active === screen.id;
              return (
                <button
                  key={screen.id}
                  type="button"
                  aria-current={isActive ? 'page' : undefined}
                  onClick={() => {
                    setMoreOpen(false);
                    setScreen(screen.id);
                  }}
                  className="flex min-h-[44px] items-center gap-3 rounded-[13px] px-3 py-2.5 text-left"
                  style={{ background: isActive ? 'oklch(1 0 0 / 0.1)' : 'transparent' }}
                >
                  <Icon className="size-[18px] shrink-0" strokeWidth={isActive ? 2.4 : 1.8} />
                  <span className="text-[14px]">{screen.label}</span>
                  {isActive ? <span className="ml-auto text-[12px]">✓</span> : null}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </>
  );
}
